<template>
  <form
    method="GET"
    class="forum-search"
  >
    <div class="form-group">
      <label for="search_forums">
        {{ gettext('Search messages:') }}
      </label>
      <div class="input-btn-group search-with-btn">
        <div class="search-bar">
          <input
            id="search_forums"
            ref="messageSearch"
            v-model="query"
            type="search"
            autocomplete="nope"
            placeholder="Search keywords, usernames, filenames, and more. Combine search terms and apply filters to help narrow your search."
            aria-describedby="search_forums-desc"
            @keyup.enter="searchMessages"
          >
          <button
            ref="messageSearchBtn"
            class="btn btn--primary btn--inline"
            @click.prevent="searchMessages"
          >
            <img
              src="/static/images/icons/icon-search-white.svg"
              width="24"
              alt="Search"
              style="display:block"
            >
          </button>
        </div>
        <div class="ai-bar">
          <div>
            <v-switch
              v-if="userHasTranslationPerm"
              v-model="$store.state.includeTranslationInSearch"
              label="Include AI-translated English version of the queried term in the search"
            />
          </div>
          <div>
            <button
              v-if="savedSearches.length > 0"
              type="button"
              title="Saved Searches"
              class="saved-searches-btn"
              @click="showSavedSearches = true"
            >
              <img src="/images/icons/saved_search_48dp.svg">
            </button>
          </div>
        </div>
        <div class="message__details-wrapper">
          <details
            id="search_forums-desc"
            class="message__details u-px-md"
          >
            <summary>How to combine search terms</summary>
            <ul class="u-mb-xs">
              <li>
                Separate terms with <code>+</code> to search for more than one term.<br>
                For example, <code>Canada + Mexico</code> will return results that contain both <code>Canada</code> and <code>Mexico</code>,
                but the terms may not always be together.<br>
                This is the same as searching for <code>Canada Mexico</code>.
              </li>
              <li>
                Separate terms with <code>|</code> to search for one or more term.<br>
                For example, <code>Canada | Mexico</code> will return results that contain either <code>Canada</code> or <code>Mexico</code>, or both.
              </li>
              <li>
                Place terms inside <code>"</code> to search for grouped terms.<br>
                For example, <code>"The Netherlands"</code> will return results that contain the words <code>The Netherlands</code> together.
              </li>
              <li>
                Start terms with <code>-</code> to exclude terms.<br>
                For example, <code>-Canada</code> will return results that do not contain the word <code>Canada</code>.
              </li>
              <li>
                Place terms inside <code>(</code> and <code>)</code> to perform a complex search.<br>
                For example, <code>(Canada + Mexico) | "The Netherlands"</code> will return a result that either contains both
                <code>Canada</code> and <code>Mexico</code>, or contains only <code>The Netherlands</code>, or contains all the provided terms.
              </li>
            </ul>
          </details>
        </div>
      </div>
    </div>

    <div class="forum-search__filter-select">
      <div class="forum-search__filter-type">
        <label for="filter_by">
          {{ gettext('Filter by:') }}
        </label>
        <div class="input-group">
          <select
            id="filter_by"
            v-model="filterBy"
          >
            <option value="">
              &nbsp;
            </option>
            <option
              v-for="(text, val) in filterTypes"
              :key="val"
              :value="val"
            >
              {{ text }}
            </option>
          </select>
        </div>
      </div>
      <div class="forum-search__filter-term">
        <template v-if="filterBy === 'posted_date'">
          <div v-if="filterMode === 'range'" class="date-range">
            <div class="date-range-field">
              <label for="start_date_filter_term">
                {{ gettext('Select start date:')}}
              </label>
              <div class="input-group">
                <input
                  id="start_date_filter_term"
                  v-model="startDateFilterTerm"
                  type="date"
                  autocomplete="nope"
                >
              </div>
            </div>
            <div class="date-range-field">
              <label for="end_date_filter_term">
                {{ gettext('Select end date:')}}
              </label>
              <div class="input-group">
                <input
                  id="end_date_filter_term"
                  v-model="endDateFilterTerm"
                  type="date"
                  autocomplete="nope"
                >
              </div>
            </div>
          </div>
          <div v-else>
            <label for="filter_term">
              {{ gettext('Select date:') }}
            </label>
            <div class="input-group">
              <input
                id="filter_term"
                v-model="filterTerm"
                type="date"
                autocomplete="nope"
                @keyup.enter="addFilterAndReset({ mode: filterMode, type: filterBy, term: filterTerm })"
              >
            </div>
          </div>
        </template>
        <template v-else-if="filterBy === 'site_name'">
          <label for="filter_term">
            {{ gettext('Filter term:') }}
          </label>
          <autocomplete-input
            :suggestions="siteNames"
            @getSuggestions="getSiteNames"
            @submitInput="submitAutocompleteInput"
            @setInput="setAutocompleteInput"
          />
        </template>
        <template v-else>
          <label for="filter_term">
            {{ gettext('Filter term:') }}
          </label>
          <div class="input-group">
            <input
              id="filter_term"
              v-model="filterTerm"
              type="text"
              autocomplete="nope"
              @keyup.enter="addFilterAndReset({ mode: filterMode, type: filterBy, term: filterTerm, fuzzy: fuzzySearch })"
            >
          </div>
        </template>
      </div>
      <div class="forum-search__filter-include">
        <div class="input-group">
          <select
            id="filter_mode"
            v-model="filterMode"
          >
            <option value="+">
              include
            </option>
            <option value="-">
              exclude
            </option>
            <template v-if="filterBy === 'posted_date'">
              <option value=">">
                greater than
              </option>
              <option value="<">
                less than
              </option>
              <option value="range">
                range
              </option>
            </template>
          </select>
        </div>
      </div>
      <div class="forum-search__add-filter">
        <button
          type="button"
          class="btn btn--sm btn--dark"
          @click.prevent="addFilter({ mode: filterMode, type: filterBy, term: filterTerm, fuzzy: fuzzySearch })"
        >
          {{ pgettext('add filter', 'Add') }}
        </button>
      </div>
    </div>
    <div class="forum-search__second-row">
      <div class="bookmark-filter">
        <label for="bookmark-filter">Filter by Bookmark:</label>
        <select
          id="bookmark-filter"
          v-model="bookmarkFilterType"
        >
          <option
            v-for="(value, key) in bookmarkFilters"
            :key="key"
            :value="key"
          >
            {{ value }}
          </option>
        </select>
      </div>
      <div class="saved-search-buttons">
        <button
          v-if="showSaveSearch"
          class="show-save-search"
          type="button"
          @click="saveSearchPopup"
        >
          Save Search
        </button>
      </div>
      <div
        v-if="enableFuzzySearch"
        class="fuzzy-search"
      >
        Enable Fuzzy Search
        <img
          class="fuzzy-search-info"
          src="/static/images/icons/icon-attention-grey.svg"
          @mouseover="showFuzzyExplaination = true"
          @mouseout="showFuzzyExplaination = false"
        >
        <input
          id="fuzzy-search"
          v-model="fuzzySearch"
          type="checkbox"
          name="fuzzy-search"
        >
        <label for="fuzzy-search" />
        <div
          v-if="showFuzzyExplaination"
          class="fuzzy-search-explaination"
        >
          Retrieved results will include approximate matches. IE. Results may contain exact matches to the filter term,
          in addition to results that are similar to the filter term.
        </div>
      </div>
    </div>
    <div
      v-if="initDocId || filters && filters.length > 0"
      class="forum-search__active-filters"
    >
      <div class="forum-search__active-filters-head">
        <p>
          {{ gettext('Active filters') }}
        </p>
        <button
          type="button"
          class="btn btn--xs btn--light forum-search__remove-all-filters"
          @click.prevent="removeAllFilters"
        >
          {{ gettext('Remove all') }}
        </button>
      </div>

      <div
        v-if="initDocId"
        class="forum-search__active-filter"
      >
        <div class="forum-search__active-filter-info">
          Doc id:
          <b>{{ initDocId }}</b>
        </div>
        <button
          :aria-label="gettext('Remove doc_id')"
          type="button"
          class="btn btn--light forum-search__remove-filter"
          @click.prevent="$emit('removeDocId', true)"
        >
          <svg
            aria-hidden="true"
            focusable="false"
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 100 100"
          >
            <path d="M90 20L80 10 50 40 20 10 10 20l30 30-30 30 10 10 30-30 30 30 10-10-30-30z" />
          </svg>
        </button>
      </div>

      <p
        v-if="showIncludedFilters"
        class="filter-header"
      >
        Included
      </p>
      <div
        v-for="(filter, index) in includedFilters"
        :key="'include' + index"
        class="forum-search__active-filter"
      >
        <active-filter
          :filter="filter"
          :filterTypes="filterTypes"
          @removeFilter="removeFilter"
        />
      </div>
      <p
        v-if="showExcludedFilters"
        class="filter-header"
      >
        Excluded
      </p>
      <div
        v-for="(filter, index) in excludedFilters"
        :key="'exclude' + index"
        class="forum-search__active-filter"
      >
        <active-filter
          :filter="filter"
          :filterTypes="filterTypes"
          @removeFilter="removeFilter"
        />
      </div>
      <p
        v-if="showGreaterThanFilters"
        class="filter-header"
      >
        Greater Than
      </p>
      <div
        v-for="(filter, index) in greaterThanFilters"
        :key="'greater than' + index"
        class="forum-search__active-filter"
      >
        <active-filter
          :filter="filter"
          :filterTypes="filterTypes"
          @removeFilter="removeFilter"
        />
      </div>
      <p
        v-if="showLessThanFilters"
        class="filter-header"
      >
        Less Than
      </p>
      <div
        v-for="(filter, index) in lessThanFilters"
        :key="'less than' + index"
        class="forum-search__active-filter"
      >
        <active-filter
          :filter="filter"
          :filterTypes="filterTypes"
          @removeFilter="removeFilter"
        />
      </div>
    </div>
    <div
      v-if="showSaveSearchPopup"
      class="save-search-container"
      @click.self="cancelSaveSearch"
    >
      <div class="save-search">
        <p
          v-if="savedSearchNameError"
          class="save-search-error"
        >
          {{ savedSearchNameError }}
        </p>
        <label
          for="saved-search-name"
        >
          Please enter a name for the saved search:
        </label>
        <input
          v-model="savedSearchName"
          id="saved-search-name"
          ref="savedSearchName"
          type="text"
          maxlength="255"
          required
          @keyup.enter="saveSearch"
          @keyup.escape="cancelSaveSearch"
        >
        <p>
          <button
            class="save-search-btn"
            type="button"
            @click="saveSearch"
          >
            Save
          </button>
          <button
            type="button"
            class="cancel-save-search"
            @click="cancelSaveSearch"
          >
            Cancel
          </button>
        </p>
      </div>
    </div>
    <div
      v-if="showSavedSearches"
      class="saved-searches-container"
      @click.self="showSavedSearches = false"
    >
      <div class="saved-searches">
        <a
          href="#"
          class="close-saved-search"
          @click.prevent="showSavedSearches = false"
        >
          X
        </a>
        <h4>Saved Searches</h4>
        <div class="search-items">
          <saved-search-item
            v-for="(search, index) in savedSearches"
            :key="index"
            :search="search"
            :index="index"
            @searchUpdated="searchUpdated"
            @deleteSearchItem="deleteSearchItem"
          />
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import axios from 'axios'
import Cookies from 'js-cookie'
import ActiveFilter from './ActiveFilter'
import AutocompleteInput from '../AutocompleteInput'
import SavedSearchItem from './SavedSearchItem.vue'

export default {
  name: 'MessageSearchForm',

  components: {
    ActiveFilter,
    AutocompleteInput,
    SavedSearchItem
  },

  props: {
    initQuery: {
      type: String,
      default: ''
    },
    initFilters: {
      type: String,
      default: ''
    },
    initDocId: {
      type: String,
      default: ''
    },
    filterTypes: {
      type: Object,
      required: true
    },
    showSaveSearch: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      filterMode: '+',
      filterBy: '',
      filterTerm: '',
      bookmarkFilterType: '',
      bookmarkFilters: {
        all: 'All Bookmarks',
        chat: 'Chat Bookmarks',
        forum: 'Forum Bookmarks',
        none: 'All Messages'
      },
      showIncludedFilters: false,
      showExcludedFilters: false,
      showGreaterThanFilters: false,
      showLessThanFilters: false,
      siteNames: [],
      userHasTranslationPerm: false,
      enableFuzzySearch: false,
      fuzzySearch: false,
      fuzzySearchFilters: [
        'site_name',
        'forum',
        'title',
        'topic',
        'username'
      ],
      showFuzzyExplaination: false,
      savedSearches: [],
      showSavedSearches: false,
      showSaveSearchPopup: false,
      savedSearchName: '',
      savedSearchNameError: null,
      endDateFilterTerm: '',
      startDateFilterTerm: '',
    }
  },

  computed: {
    query: {
      get() {
        return this.$store.state.query
      },
      set(query) {
        this.$store.commit('query', query)
        return query
      },
    },
    filters: {
      get() {
        return this.$store.state.filters
      },
      set(filters) {
        this.$store.commit('addFilters', filters)
        return filters
      }
    },
    termObj() {
      return {
        type: this.filterBy,
        term: this.filterTerm,
      }
    },
    includedFilters() {
      return this.filters.filter(item => item.mode === '+')
    },
    excludedFilters() {
      return this.filters.filter(item => item.mode === '-')
    },
    greaterThanFilters() {
      return this.filters.filter(item => item.mode === '>')
    },
    lessThanFilters() {
      return this.filters.filter(item => item.mode === '<')
    }
  },
  watch: {
    bookmarkFilterType(option) {
      if (option === 'none' || option === '') {
        this.resetBookmarkFilter()
      } else {
        this.bookmarkFilter(option)
      }
    },
    filters() {
      this.filterModeCheck()
    },
    filterBy() {
      this.filterTerm = '' // reset the filterTerm when filterBy changes
      this.siteNames = []
      if (this.filterBy === 'posted_date') {
        this.filterMode = 'range'
        let today = new Date()
        this.filterTerm = today.toISOString().split('T')[0].replace('/', '-')
      }
      if (this.fuzzySearchFilters.includes(this.filterBy)) {
        this.enableFuzzySearch = true
      } else {
        this.enableFuzzySearch = false
      }
    }
  },
  mounted() {
    this.filterModeCheck()
    this.userHasTranslationPerm = !!window.can_translate_content_using_ai
    axios.get('/en/message-search/saved-searches/list/').then(response => {
      this.savedSearches = response.data
    })
  },
  methods: {
    readableDate(timestamp) {
      let date = new Date(timestamp * 1000)
      return date.toUTCString()
    },
    addFilterAndReset(filter) {
      this.addFilter(filter)
      document.getElementById('filter_term').blur()
      document.getElementById('filter_by').focus()
    },
    addFilter(filter) {
      if (filter.type === 'bookmarked') {
        const bookmarkIndex = this.filters.findIndex(
          (filter) => filter.type === 'bookmarked'
        )
        if (bookmarkIndex !== -1) {
          this.filters.splice(bookmarkIndex, 1, filter)
        } else {
          this.filters.push(filter)
        }
        this.filterTerm = filter
      } else {
        this.filters = this.filters.filter((filter) => filter.type !== 'bookmarked') // filter out filter.type == bookmarked
        if (!(this.filterBy === 'posted_date' && this.filterMode === 'range')) {
          this.filters.push(filter)
        }
      }
      if (this.filterBy === 'posted_date') { // filterBy refers to the input role's "Filter by" input field
        // get local timezone offset and convert to milliseconds
        let timezoneOffset = new Date().getTimezoneOffset() * 60 * 1000
        if (this.filterMode === 'range') {
          if (this.startDateFilterTerm === '') {
            let startDateFilterEl = document.getElementById('start_date_filter_term')
            startDateFilterEl?.setCustomValidity('Start date is required.')
            startDateFilterEl?.reportValidity()
            return
          } else if (this.endDateFilterTerm === '') {
            let endDateFilterEl = document.getElementById('end_date_filter_term')
            endDateFilterEl?.setCustomValidity('End date is required.')
            endDateFilterEl?.reportValidity()
            return
          }
          // Date.parse() will only give time at the local timezone (even with adding T00:00:00.000Z),
          // and so we need to add the tz offset
          let startDateFilter = {
            type: 'posted_date',
            term: (Date.parse(this.startDateFilterTerm) + timezoneOffset) / 1000,
            mode: '>'
          }
          let endDateFilter = {
            type: 'posted_date',
            term: (Date.parse(this.endDateFilterTerm) + timezoneOffset) / 1000,
            mode: '<'
          }
          this.filters.push(startDateFilter)
          this.filters.push(endDateFilter)
          this.$store.commit('addFilters', [startDateFilter])
          this.$store.commit('addFilters', [endDateFilter])
          this.filterMode = '+'
          this.filterBy = ''
          this.startDateFilterTerm = ''
          this.endDateFilterTerm = ''
          return
        } else {
          let dateString = filter.term
          let date = (Date.parse(dateString) + timezoneOffset) / 1000
          filter.term = date
        }
      }
      if (filter.type && filter.term) {
        console.log('----------', filter)
        this.$store.commit('addFilters', [filter])
        this.filterMode = '+'
        this.filterBy = ''
        this.filterTerm = ''
        this.fuzzySearch = false
        this.enableFuzzySearch = false
      }
    },
    bookmarkFilter() {
      this.filterBy = 'bookmarked'
      this.addFilter({type: this.filterBy, term: this.bookmarkFilterType})
    },
    cancelSaveSearch() {
      this.savedSearchName = ''
      this.savedSearchNameError = null
      this.showSaveSearchPopup = false
    },
    resetBookmarkFilter() {
      const bookmarkIndex = this.filters.findIndex((filter) => filter.type === 'bookmarked')
      if (bookmarkIndex !== -1) {
        this.filters.splice(bookmarkIndex, 1)
      }
      this.filterBy = ''
      this.filterTerm = ''
    },
    removeFilter(e) {
      let targetMode = e.target.getAttribute('data-mode')
      let targetType = e.target.getAttribute('data-type')
      let targetTerm = e.target.getAttribute('data-term')
      this.$store.commit('removeFilter', {mode: targetMode, type: targetType, term: targetTerm})
      if (targetType === 'bookmarked') {
        this.resetBookmarkFilter()
        this.bookmarkFilterType = ''
      }
    },
    removeAllFilters() {
      if (this.initDocId) {
        // this emission was causing bugs to occur so we're only doing it when doc id is present
        // should only happen by internal users
        this.$emit('removeDocId')
      }
      this.$store.commit('emptyFilters')
      this.bookmarkFilterType = ''
    },
    searchMessages(e) {
      // for some reason the 'enter key' presses on the filters are triggering this event
      // when they shouldn't be. the usual 'stop' and 'prevent' guards aren't working either.
      // so instead we have to make sure that the triggering element is one of the search inputs.
      let target = e.explicitOriginalTarget ? e.explicitOriginalTarget : e.target
      if (target === this.$refs.messageSearch || target === this.$refs.messageSearchBtn) {
        this.$emit('manualMessageSearchStart')
      }
    },
    filterModeCheck() {
      this.showIncludedFilters = false
      this.showExcludedFilters = false
      this.showGreaterThanFilters = false
      this.showLessThanFilters = false
      this.filters.forEach(filter => {
        if (filter.mode === '+') this.showIncludedFilters = true
        if (filter.mode === '-') this.showExcludedFilters = true
        if (filter.mode === '>') this.showGreaterThanFilters = true
        if (filter.mode === '<') this.showLessThanFilters = true
      })
    },
    getSiteNames(term) {
      this.filterTerm = term
      axios.get(`/en/message-search/get_site_names/?query=${this.filterTerm}`)
        .then((response) => {
          this.siteNames = response.data
        })
    },
    submitAutocompleteInput(inputValue) {
      // autocomplete inputs need this extra step to pass the inputValue into the filterTerm and
      // to reset it's suggestions.
      this.filterTerm = inputValue
      this.addFilterAndReset({ mode: this.filterMode, type: this.filterBy, term: this.filterTerm, fuzzy: this.fuzzySearch })
      this.siteNames = []
    },
    setAutocompleteInput(inputValue) {
      // the filter term isn't set to the autocomplete input value when using the filter 'add' button.
      // this simple function just makes sure it is (child can only set parent variables through events).
      this.filterTerm = inputValue
    },
    saveSearchPopup() {
      this.savedSearchName = this.query || this.filters[0]['type'] + ': ' + this.filters[0]['term']
      this.showSaveSearchPopup = true
      this.$nextTick(() => this.$refs.savedSearchName.focus())
    },
    saveSearch() {
      this.savedSearchNameError = null
      if (!this.$refs.savedSearchName.checkValidity()) {
        this.$refs.savedSearchName.reportValidity()
        this.$refs.savedSearchName.focus()
        return
      }
      const csrftoken = Cookies.get('csrftoken')
      let search = {
        'name': this.savedSearchName,
        'search': window.location.search
      }
      axios.post('/en/message-search/saved-searches/add/', search, {'headers': {'X-CSRFToken': csrftoken}}).then(response => {
        this.showSaveSearchPopup = false
        this.savedSearches.push(search)
      }).catch((err) => {
        this.savedSearchNameError = err.response.data
      })
    },
    deleteSearchItem(search) {
      const csrftoken = Cookies.get('csrftoken')
      let data = {
        'name': search.name
      }
      axios.post('/en/message-search/saved-searches/delete/', data, {'headers': {'X-CSRFToken': csrftoken}}).then(response => {
        console.log(response)
        if (response.status === 200) {
          this.savedSearches = this.savedSearches.filter(item => {
            if (item.name !== search.name) return item
          })
        }
      })
    },
    searchUpdated(update) {
      this.savedSearches[update.index].name = update.name
    }
  },
}
</script>
<style>
.bookmark-filter {
  width: 28.5%;
}
.search-with-btn button {
  vertical-align: top;
  padding-top: 0.4em;
  padding-bottom: 0.4em;
  border: 2px solid #c22c3f;
}
#search_forums {
  width: calc(100% - 78px);
}
#filter_mode {
  width: 145px;
  min-width: 145px;
}
.message__details--forum {
  cursor: pointer;
}
.filter-header {
  margin-bottom: 4px;
  padding-top: 10px;
  border-top: 1px solid #fff;
}
.filter-header:last-of-type {
  margin-top: 20px;
}
.search-bar {
  display: flex;
  justify-items: stretch;
  gap: 5px;
}
.forum-search__second-row {
  position: relative;
}
.fuzzy-search {
  position: absolute;
  right: 253px;
  top: 0;
}
#fuzzy-search {
  width: 0;
  height: 0;
  display: none;
}
label[for="fuzzy-search"] {
  display: inline-block;
  width: 50px;
  height: 30px;
  background-color: #ccc;
  border: 2px solid #ccc;
  border-radius: 100px;
  position: relative;
  cursor: pointer;
  transition: 0.2s;
  vertical-align: middle;
  margin-left: 6px;
}
input[type="checkbox"]#fuzzy-search + label:before,
label[for="fuzzy-search"]:before {
  display: none;
}
input[type="checkbox"]#fuzzy-search + label:after {
  opacity: 1;
  transition: 0.2s;
}
label[for="fuzzy-search"]:after {
  content: '';
  width: 24px;
  height: 24px;
  background-color: #fff;
  position: absolute;
  border-radius: 70px;
  top: 1px;
  left: 1px;
  transition: 0.2s;
  opacity: 1;
}
input[name="fuzzy-search"]:checked + label[for="fuzzy-search"] {
  background-color: #c22c3f;
  border: 2px solid #c22c3f;
}
input[name="fuzzy-search"]:checked + label[for="fuzzy-search"]:after {
  left: calc(100% - 1px);;
  transform: translateX(-100%);
  top: 1px;
  width: 24px;
  height: 24px;
  border: 0;
}
.fuzzy-search-info {
  width: 18px;
  height: 18px;
}
.fuzzy-search-explaination {
  position: absolute;
  top: 24px;
  left: 205px;
  font-size: 14px;
  background: #fff;
  width: 250px;
  padding: 10px;
  border: 1px solid #ccc;
  z-index: 10;
}
.saved-search-buttons {
  margin-top: 20px;
}
.save-search-container,
.saved-searches-container {
  width: 100vw;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 100;
}
.save-search,
.saved-searches {
  width: 50vw;
  position: fixed;
  top: 30%;
  left: 50%;
  transform: translate(-50%, -50%);
  border: 2px solid #ccc;
  background: #fff;
  padding: 30px;
  box-shadow: 10px 10px 5px 0px rgba(0, 0, 0, 0.2);
  z-index: 101;
}
.save-search p {
  text-align: center;
  margin-top: 20px;
}
.close-saved-search {
  position: absolute;
  top: 5px;
  right: 10px;
  font-weight: bold;
  text-decoration: none;
}
.search-items {
  display: flex;
  gap: 10px 4px;
  flex-wrap: wrap;
}
.search-item {
  width: 33%;
  border: 1px solid #ccc;
  padding: 4px 8px;
  position: relative;
}
.delete-search-item,
.cancel-update-item {
  position: absolute;
  right: 0;
  height: 24px;
  width: 24px;
}
.cancel-update-item img {
  width: 80%;
}
.edit-search-item,
.update-search-item {
  position: absolute;
  right: 25px;
  height: 24px;
  width: 24px;
}
.show-save-search,
.save-search-btn {
  background-color: #a42334;
  color: #fff;
  padding: 10px;
  border-radius: 6px;
  margin-right: 10px;
}
.saved-search-input {
  width: 80%;
  padding: 0px 6px;
}
.cancel-save-search {
  background-color: #fff;
  color: #a42334;
  border: 1px solid #a42334;
  padding: 10px;
  border-radius: 6px;
}
.save-search p.save-search-error {
  margin-top: 0;
  color: #a42334;
  text-align: left;
}
.ai-bar {
  display: flex;
}
.ai-bar > div {
 width: 50%;
}
.ai-bar > div:last-of-type {
  text-align: right;
}
.search-with-btn button.saved-searches-btn {
  border: none;
}
.date-range {
  display: flex;
  flex-direction: row;
  gap: 1rem;
}
.date-range-field {
  width: 100%;
}
</style>
